import { NextPage } from 'next';
import {
  Container,
  Typography,
  Box,
  Stack,
  Divider,
  Link,
} from '@mui/material';

import { useIntl } from '@klokhuis/core/Intl';
import { PageLayout } from '@klokhuis/core/PageLayout';
import { ROUTES } from '@klokhuis/core/Routing';

const Terms: NextPage = () => {
  const { T } = useIntl();

  return (
    <PageLayout
      pageTitle={T('LBL_TERMS_TITLE')}
      metaDescription={T('LBL_TERMS_META')}
      route={ROUTES.TermsAndConditions}
      showPropertyLinksGrid={false}
    >
      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Last Updated: February 22, 2026
          </Typography>
        </Box>

        {/* Content Sections */}
        <Stack spacing={5}>
          {/* Section 1 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              By accessing or using FindMyProps.com, you agree to be bound by
              these Terms and Conditions and our Privacy Policy. If you do not
              agree, you must not use our Platform. We reserve the right to
              modify these terms at any time.
            </Typography>
          </Box>

          <Divider />

          {/* Section 2 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              2. Nature of Service
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              FindMyProps.com is a <strong>facilitating platform only</strong>.
              We connect buyers, sellers, tenants, landlords, brokers, and
              agents. We do not engage in real estate transactions, act as
              brokers, verify property accuracy, provide legal or financial
              advice, handle property payments, or mediate disputes between
              users.
            </Typography>
          </Box>

          <Divider />

          {/* Section 3 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              3. User Registration & Account
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              To use our Platform, you must be 18+ years of age and provide
              accurate information. You are responsible for maintaining account
              confidentiality and all activities on your account. Unauthorized
              access must be reported immediately. Violation of terms may result
              in account suspension.
            </Typography>
          </Box>

          <Divider />

          {/* Section 4 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              4. User Conduct & Prohibited Activities
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              You must not post false information, list properties without
              authorization, engage in discrimination, spam, harassment, or
              bypass platform fees. Scraping data, violating laws, or breaching
              RERA regulations is strictly prohibited. Violations will result in
              account termination.
            </Typography>
          </Box>

          <Divider />

          {/* Section 5 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              5. Property Listings
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              All listing information must be accurate and truthful. You must
              have legal authority to advertise properties and comply with RERA
              regulations. Include clear, genuine photographs and update
              listings promptly when information changes. FindMyProps.com
              reserves the right to remove listings that violate these terms.
            </Typography>
          </Box>

          <Divider />

          {/* Section 6 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              6. Your Responsibility: Due Diligence
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              You must independently verify property ownership, legal approvals,
              RERA registration, measurements, builder credentials, market
              prices, and locality information. Always visit properties
              personally, inspect thoroughly, verify seller identity, consult
              independent legal counsel, and seek professional financial advice.
            </Typography>
          </Box>

          <Divider />

          {/* Section 7 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              7. Payment Terms
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Platform fees are charged for listing and feature access only.
              FindMyProps.com handles ZERO property payments. Never make
              property payments through our platform—all payments must go
              through proper legal channels and banks. Refunds are available for
              technical errors, duplicate charges, or service cancellation
              within 7 days.
            </Typography>
          </Box>

          <Divider />

          {/* Section 8 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              8. Limitation of Liability
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              The Platform is provided "as is" without warranties.
              FindMyProps.com is not liable for fraudulent listings, property
              disputes, financial losses, unauthorized access, or third-party
              conduct. Our total liability is limited to fees paid in the
              preceding 12 months.
            </Typography>
          </Box>

          <Divider />

          {/* Section 9 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              9. Dispute Resolution
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              FindMyProps.com is not responsible for disputes between users. All
              property-related disputes must be resolved independently. Disputes
              between you and FindMyProps.com shall be governed by Indian law
              and resolved through arbitration in accordance with the
              Arbitration and Conciliation Act, 1996.
            </Typography>
          </Box>

          <Divider />

          {/* Section 10 */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              10. RERA Compliance
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              RERA registration is mandatory for all projects. Always verify the
              RERA registration number, required approvals, accurate carpet area
              calculations, and distinctions between built-up and super built-up
              areas. Do not invest in projects without official RERA
              approval—such investments are high-risk and entirely your
              responsibility.
            </Typography>
          </Box>

          <Divider />

          {/* Safety Guidelines */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Safety Guidelines
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Never pay visit charges, gate pass fees, or booking amounts. Never
              share sensitive banking information, transfer money before legal
              verification, send payments to personal accounts, trust deals that
              seem too good to be true, or send documents to unverified
              contacts.
            </Typography>
          </Box>

          <Divider />

          {/* Contact & Support */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Contact & Support
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              For support, reach out to <strong>support@findmyprops.com</strong>
              . To report abuse, use the "Report" button on listings or email
              support. For grievances, contact{' '}
              <strong>grievances@findmyprops.com</strong>.
            </Typography>
          </Box>

          <Divider />

          {/* Final Acknowledgment */}
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="caption" color="textSecondary">
              By using FindMyProps.com, you acknowledge that you have read and
              agree to be bound by these Terms and Conditions.
            </Typography>
            <Typography
              variant="caption"
              display="block"
              color="textSecondary"
              sx={{ mt: 2 }}
            >
              © 2026 FindMyProps.com. All Rights Reserved. | Version 1.0
            </Typography>
          </Box>
        </Stack>
      </Container>
    </PageLayout>
  );
};

export default Terms;
